import { Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    IRetryPolicy,
    LogLevel,
    RetryContext
} from '@microsoft/signalr';
import { UtilsService } from 'abp-ng2-module';
import {
    acceptConversationSignal,
    CONVERSATION_COUNTS_CHANGED_EVENT,
    ConversationCountsChangedSignal
} from '@app/shared/common/conversations/conversation-signal';
import {
    acceptDiscoveryJobSignal,
    DISCOVERY_JOB_CHANGED_EVENT,
    DiscoveryJobChangedSignal
} from '@app/shared/common/discovery-job/discovery-job-signal';
import { DiscoveryJobStatusService } from '@app/shared/common/discovery-job/discovery-job-status.service';
import { AppComponentBase } from '@shared/common/app-component-base';
import { AppConsts } from '@shared/commonHelper/AppConsts';
import { isFromThisTab } from '@shared/helpers/BrowserTabHelper';
import { BehaviorSubject, Subject } from 'rxjs';
import { routeClientUpdate } from './client-update-route';

/** The SignalR push `getClientUpdate` (`ClientUpdateSignalDto` on the server): people or companies changed in this workspace. */
export interface ClientUpdateSignal {
    tenantId: number;
    /** `leadSummary` (Lead Pipeline LP7): only these people's Living Lead Summary / AI next action changed - routed to
     *  `leadSummaryUpdates`, never to `clientUpdates`. `leadStages` (LP9, PER-51): client stage settings changed, the ids are
     *  STAGE ids - routed to `leadStageUpdates` only. */
    entityType: 'contact' | 'company' | 'leadSummary' | 'leadStages' | 'lostReasons';
    /** The changed record when one changed; 0 when several did (see `entityIds`). */
    entityId: number;
    /** Every record the change touched. Missing on a push from an older server. */
    entityIds?: number[];
    /** The changed person's company (the Outlook ingest sets it); 0 when not known. */
    companyId?: number;
    /** The tab whose request made the change; null when no tab did (the Outlook ingest). */
    originTabId?: string | null;
}

/** Whether `signal` names this person (`'contact'`) or company, alone or among several. */
export function clientUpdateTouches(signal: ClientUpdateSignal, entityType: ClientUpdateSignal['entityType'], id: number): boolean {
    if (!signal || signal.entityType !== entityType || !id) {
        return false;
    }
    return Number(signal.entityId) === id || (signal.entityIds || []).some((changedId) => Number(changedId) === id);
}

/**
 * Never-give-up retry policy for the hub's built-in automatic reconnect:
 * 0s, 2s, 5s, 10s, 20s, then every 30s forever (each with up to 1s of jitter so a
 * fleet of clients doesn't stampede the service the moment it comes back).
 * Returning null would stop reconnecting — this policy never does.
 */
class PersistentRetryPolicy implements IRetryPolicy {
    private static readonly STEPS_MS = [0, 2000, 5000, 10000, 20000];
    private static readonly CAP_MS = 30000;
    private static readonly JITTER_MS = 1000;

    nextRetryDelayInMilliseconds(retryContext: RetryContext): number {
        const base = retryContext.previousRetryCount < PersistentRetryPolicy.STEPS_MS.length
            ? PersistentRetryPolicy.STEPS_MS[retryContext.previousRetryCount]
            : PersistentRetryPolicy.CAP_MS;
        return base + Math.floor(Math.random() * PersistentRetryPolicy.JITTER_MS);
    }
}

/**
 * Owns the app's single Azure SignalR connection and keeps it alive for the whole session —
 * the "self-healing" contract is:
 *
 *  1. One HubConnection, built directly with @microsoft/signalr (the abp.signalr-client.js
 *     bootstrap script is gone — its automatic-reconnect callback referenced undeclared
 *     variables and threw, and the old triple connect()/startConnection() bootstrap opened
 *     up to three concurrent connections per tab).
 *  2. Event handlers are registered on EVERY connection object we build. They live on the
 *     object, not the service — the old service-level "already registered" guard left any
 *     rebuilt connection deaf: state said Connected, but no getUpdatedTaskCard/... handlers
 *     were attached, so the UI silently went stale until a manual refresh.
 *  3. The built-in automatic reconnect never gives up (PersistentRetryPolicy), and onclose —
 *     which then only fires on hard failures or explicit stops — always schedules a restart,
 *     even while the tab is hidden.
 *  4. A dedicated-worker watchdog checks health every 25s. Worker timers are not throttled in
 *     hidden tabs (main-thread timers degrade to once a minute, or pause entirely in frozen
 *     tabs), so a backgrounded tab still detects a dead socket and heals it.
 *  5. Wake-up hooks (tab visible, window focus, network online, bfcache pageshow, lifecycle
 *     resume) verify the connection immediately and skip any pending backoff wait; a
 *     connection wedged mid-reconnect is torn down and rebuilt from scratch.
 *  6. Reconcile-on-return: broadcasts sent while the socket was down are lost (no replay), and
 *     pushes during the Azure SignalR group-rejoin window are silently dropped — so after every
 *     real reconnect, and when returning to a tab that was hidden long enough to have missed
 *     things, connectionReestablished/calendarEventUpdates fire and surfaces re-pull from the
 *     server. My Day (desktop + mobile) and the calendar surfaces subscribe to these.
 */
@Injectable()
export class UserSignalrService extends AppComponentBase implements OnDestroy {
    userStateUpdates: Subject<any> = new Subject();
    taskUpdates: Subject<any> = new Subject();
    commentUpdates: Subject<any> = new Subject();
    historyUpdates: Subject<any> = new Subject();
    newTaskAdded: Subject<any> = new Subject();
    eventUpdates: Subject<any> = new Subject();
    mteamsStatusUpdates: Subject<any> = new Subject();
    workSummaryRequiredChanged: Subject<any> = new Subject();
    // Custom Project Label (Labels & Terminology): a tenant admin changed the Project label.
    labelUpdates: Subject<any> = new Subject();
    calendarFeatureUpdates: Subject<any> = new Subject();
    // A calendar event was created / edited / deleted somewhere in the tenant — open calendars should refresh.
    calendarEventUpdates: Subject<{ tenantId: number }> = new Subject();
    // Calendar access/booking changed (a grant added/removed, or own visibility changed) in the tenant —
    // open calendars + the event form should re-resolve who can be seen/booked (E10 / TC2.2).
    calendarAccessUpdates: Subject<{ tenantId: number }> = new Subject();
    tagUpdates: Subject<any> = new Subject();
    botUpdates: Subject<any> = new Subject();
    // Billing (1c): a team member asked this user - the firm's owner - to choose a plan, and Teams could not deliver it.
    // Sent only to this user's isolation group by BillingAppService.AskAdminToUpgrade.
    billingUpgradeRequests: Subject<{ requesterName: string; message: string }> = new Subject();
    contactsImport: Subject<any> = new Subject();
    taskWatched: Subject<any> = new Subject();
    taskUnwatched: Subject<any> = new Subject();
    projectUpdates: Subject<any> = new Subject();
    // Contributor-related events broadcast by the backend. Payload shape (planned):
    //   { taskId, taskTitle, actorUserId, actorUserName, kind, ...extras }
    // kind in:
    //   'stepAssignedToContributor', 'allContributorStepsComplete',
    //   'taskMarkedCompleteByOther', 'contributorStepForceClosed',
    //   'contributorAdded', 'contributorRemoved'
    contributorUpdates: Subject<any> = new Subject();
    updatedProjectCard: Subject<any> = new Subject();
    pinnedProjectInfo: Subject<any> = new Subject();
    /**
     * Favorites changed for the current user — any type, from FavoriteAppService's `getPinned`.
     * Project events are additionally forwarded to `pinnedProjectInfo` (see the handler), so anything
     * that only cares about projects can stay on that channel.
     */
    pinnedFavoriteInfo: Subject<any> = new Subject();
    /**
     * Real-time task-timer state changes broadcast by the backend's
     * TimeTrackingAppService whenever the current user starts or stops a
     * timer (on any of their sessions). Payload shape:
     *   { userId, tenantId, action: 'start' | 'stop', activeTimer: { id, taskId, taskName, startTime, userId } | null }
     * TimeTrackingService subscribes to this and pushes the active timer into
     * `activeTimer$` so mobile/desktop stay in sync without a manual refresh.
     */
    timerUpdates: Subject<any> = new Subject();
    /**
     * Auto-shift cascade events from TaskAppService.UpdateDates. Fires when changing
     * a blocker's dates causes one or more downstream blocked tasks to shift forward.
     * Payload shape (from BE): { triggerTaskIds: number[], affectedTasks: CascadeShiftResult[] }
     * The app-level component listens to this and renders a 10-second undo toast
     * (per the Task Dependencies spec) when 2+ tasks moved.
     */
    cascadeAppliedUpdates: Subject<any> = new Subject();
    /**
     * People or companies changed somewhere else: another user's save, this user's save in ANOTHER tab, the Outlook
     * meeting ingest, or an external tool. A change made in this tab never arrives here (see the handler).
     */
    clientUpdates: Subject<ClientUpdateSignal> = new Subject();

    /**
     * Lead Pipeline LP7: the background summary job changed the Living Lead Summary (or the AI next action) of people. Only
     * the Pipeline board and the Lead journey banner listen: nothing else on screen shows it, so it never reaches
     * `clientUpdates` (no "Updates available", no drawer or picker reload).
     */
    leadSummaryUpdates: Subject<ClientUpdateSignal> = new Subject();

    /**
     * Lead Pipeline LP9 (PER-51, user 2026-10-21 "update task panel straight away"): client stage settings changed in another
     * tab or by another user (Next step, How, No follow-up, name, days, order, a new or deleted stage; `entityIds` = stage
     * ids). The Lead surfaces reload the stage list at once (`LeadLifecycleClientService`, via `LEAD_PIPELINE_PUSHES`); it
     * never reaches `clientUpdates`.
     */
    leadStageUpdates: Subject<ClientUpdateSignal> = new Subject();

    /**
     * Configurable lost reasons (story rule 23): the tenant's lost reasons, their order, the "require a reason" switch or a Lost
     * stage's default changed in another tab or by another user (`entityIds` = reason ids). The reason list behind every "Mark
     * lost" picker reloads at once (`LostReasonCatalogService`, via `LEAD_PIPELINE_PUSHES`); it never reaches `clientUpdates`.
     */
    lostReasonUpdates: Subject<ClientUpdateSignal> = new Subject();
    /**
     * Conversation counts may have changed for this user (the nav badge, a project chip or tab dot, an open overlay's
     * tabs): a write whose audience includes this user, from another user or from this user in any tab. A trigger
     * only, with ids and no counts (GLOBAL_CONVERSATIONS_SLICE2_SPEC.md §4.6, D48): consumers refetch, and re-enter
     * Angular's zone themselves, since it fires outside it. This tab's own posts, edits, resolves and legacy reads DO
     * arrive here (the server sends them with no originTabId, so this tab reconciles its optimistic change too); only
     * the echo of this tab's SetReadState, AutoReadAmbient and Undo is dropped (SD2-10, see the handler).
     */
    conversationCountsChanged: Subject<ConversationCountsChangedSignal> = new Subject();

    public userHub: HubConnection = null;
    public isUserConnected = false;
    public signalRConnectionState = new BehaviorSubject<boolean>(false);

    /**
     * Fires when surfaces should re-pull their data from the server because SignalR pushes may have
     * been missed: after every RE-connect (never on the very first connect — the initial page load
     * fetches on its own), and when the user returns to a tab that was hidden long enough (or lost
     * the socket while hidden). My Day desktop/mobile subscribe to this; calendar surfaces get the
     * equivalent nudge via calendarEventUpdates.
     */
    connectionReestablished: Subject<void> = new Subject();

    // --- self-healing connection core state ---
    private connection: HubConnection = null;
    private connectionToken = '';
    private startPromise: Promise<void> = null;
    private restartTimer: ReturnType<typeof setTimeout> = null;
    private restartAttempt = 0;
    private downSince = 0;
    private hiddenAt = 0;
    private sawDropWhileHidden = false;
    private lastReconcileAt = 0;
    private lastForceRestartAt = 0;
    private hasConnectedOnce = false;
    private isInitialized = false;
    private watchdogWorker: Worker = null;
    private watchdogWorkerUrl = '';
    private watchdogFallbackTimer: ReturnType<typeof setInterval> = null;
    private visibilityHandler: () => void = null;
    private wakeHandler: () => void = null;

    /** Health-check cadence; runs in a worker so hidden tabs keep it (main-thread timers are throttled). */
    private static readonly WATCHDOG_INTERVAL_MS = 25000;
    /** Backoff for our own restart loop (used when a start attempt fails outright). */
    private static readonly RESTART_BACKOFF_MS = [0, 2000, 5000, 10000, 20000, 30000];
    /** Connecting/Reconnecting for longer than this = wedged (hung negotiate, rotated token) → rebuild. */
    private static readonly STUCK_LIMIT_MS = 90000;
    /** On a wake event, a connection already down this long gets rebuilt instead of waiting out its backoff. */
    private static readonly WAKE_FORCE_RESTART_MS = 5000;
    /** Rapid focus/online bursts must not tear the connection down repeatedly. */
    private static readonly FORCE_RESTART_DEBOUNCE_MS = 10000;
    /** Hidden for longer than this ⇒ assume pushes were missed and reconcile on return. */
    private static readonly STALE_AFTER_HIDDEN_MS = 60000;
    /** Visibility-driven reconciles are debounced this hard (reconnect-driven ones always fire). */
    private static readonly RECONCILE_DEBOUNCE_MS = 15000;
    /** 2× the client default — tolerates timer throttling delaying keep-alive processing in hidden tabs. */
    private static readonly SERVER_TIMEOUT_MS = 60000;
    private static readonly KEEP_ALIVE_MS = 15000;

    constructor(
        injector: Injector,
        public _zone: NgZone,
        // Adaptive Onboarding / Organization Intelligence: the root consumer of `discoveryJobChanged` (see the handler).
        private readonly _discoveryJobStatus: DiscoveryJobStatusService
    ) {
        super(injector);
    }

    init(): void {
        if (this.isInitialized) {
            return;
        }
        this.isInitialized = true;
        // Everything here is timers + socket traffic — keep it out of Angular's zone so the
        // watchdog and keep-alives don't trigger change detection. Consumers of the subjects
        // re-enter the zone themselves (e.g. mine.component wraps its reload in NgZone.run).
        this._zone.runOutsideAngular(() => {
            this.setupWakeListeners();
            this.startWatchdog();
            this.ensureConnected();
        });
    }

    ngOnDestroy(): void {
        if (this.restartTimer) {
            clearTimeout(this.restartTimer);
            this.restartTimer = null;
        }
        if (this.watchdogFallbackTimer) {
            clearInterval(this.watchdogFallbackTimer);
            this.watchdogFallbackTimer = null;
        }
        if (this.watchdogWorker) {
            this.watchdogWorker.terminate();
            this.watchdogWorker = null;
        }
        if (this.watchdogWorkerUrl) {
            URL.revokeObjectURL(this.watchdogWorkerUrl);
            this.watchdogWorkerUrl = '';
        }
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }
        if (this.wakeHandler) {
            document.removeEventListener('resume', this.wakeHandler);
            window.removeEventListener('online', this.wakeHandler);
            window.removeEventListener('focus', this.wakeHandler);
            window.removeEventListener('pageshow', this.wakeHandler);
            this.wakeHandler = null;
        }
        this.dropConnection();
    }

    /**
     * Idempotent "make sure we are (getting) connected" — the single entry point every heal
     * path funnels through. Safe to call from anywhere at any frequency.
     */
    private ensureConnected(): void {
        let freshToken = '';
        let starting: HubConnection = null;
        let startAttempt: Promise<void> = null;

        if (this.startPromise) {
            return; // a start is already in flight
        }

        // If the auth token rotated since this connection's URL was baked (re-login, token
        // refresh), a non-connected hub can only ever fail its negotiates — rebuild from the
        // current cookie. A healthy connected hub is left alone: the token only matters at
        // negotiate time.
        freshToken = this.currentAuthToken();
        if (this.connection
            && this.connectionToken !== freshToken
            && this.connection.state !== HubConnectionState.Connected) {
            this.dropConnection();
        }

        if (!this.connection) {
            if (!freshToken) {
                return; // logged out — nothing to authenticate with; the watchdog will re-check
            }
            this.connection = this.buildConnection(freshToken);
            this.userHub = this.connection;
        }

        if (this.connection.state !== HubConnectionState.Disconnected) {
            return; // connected, or the built-in automatic reconnect is already on it
        }

        if (this.downSince === 0) {
            this.downSince = Date.now(); // lets the watchdog catch a hung start (fetch has no timeout)
        }

        starting = this.connection;
        startAttempt = starting.start()
            .then(() => {
                if (this.startPromise === startAttempt) {
                    this.startPromise = null;
                }
                if (this.connection !== starting) {
                    return; // superseded by a force-restart while this start was in flight
                }
                this.handleConnectionUp();
            })
            .catch(() => {
                if (this.startPromise === startAttempt) {
                    this.startPromise = null;
                }
                if (this.connection !== starting) {
                    return;
                }
                this.handleConnectionDown();
                this.scheduleRestart();
            });
        this.startPromise = startAttempt;
    }

    private buildConnection(token: string): HubConnection {
        const url = AppConsts.remoteServiceBaseUrl + '/signalr?'
            + AppConsts.authorization.encrptedAuthTokenName + '=' + encodeURIComponent(token);
        const connection = new HubConnectionBuilder()
            // Default negotiation: WebSockets with SSE/long-polling fallback, and it follows the
            // Azure SignalR negotiate redirect. Zero's auth middleware reads enc_auth_token off
            // the /signalr query string.
            .withUrl(url)
            .withAutomaticReconnect(new PersistentRetryPolicy())
            .configureLogging(LogLevel.Error)
            .build();

        connection.serverTimeoutInMilliseconds = UserSignalrService.SERVER_TIMEOUT_MS;
        connection.keepAliveIntervalInMilliseconds = UserSignalrService.KEEP_ALIVE_MS;
        this.connectionToken = token;

        // Handlers live on the connection OBJECT and must be registered on every object we build —
        // never behind a service-level "already registered" flag (that left rebuilt connections
        // deaf: Connected, but nothing listening, so the UI went stale until a manual refresh).
        this.registerUserEvents(connection);

        // Lifecycle handlers are guarded against a superseded object: a force-restart may have
        // replaced this connection while its stop/close was still settling, and a late onclose
        // from the dying object must not knock over the healthy new one.
        connection.onreconnecting(() => {
            if (this.connection !== connection) {
                return;
            }
            this.handleConnectionDown();
        });
        connection.onreconnected(() => {
            if (this.connection !== connection) {
                return;
            }
            // The reconnected socket has a NEW connectionId; the hub's OnConnectedAsync re-joins the
            // tenant/user isolation groups server-side (it can lag a beat — the reconcile below
            // covers anything pushed during that group-rejoin window).
            this.handleConnectionUp();
        });
        connection.onclose(() => {
            if (this.connection !== connection) {
                return;
            }
            // With the infinite retry policy this only fires on hard failures or explicit stops —
            // always heal, even while the tab is hidden.
            this.handleConnectionDown();
            this.scheduleRestart();
        });

        return connection;
    }

    /** Drop the current connection object entirely; late events from it are ignored by the guards. */
    private dropConnection(): void {
        const dying = this.connection;

        this.connection = null;
        this.userHub = null;
        this.startPromise = null;
        if (dying) {
            try {
                dying.stop().catch(() => undefined);
            } catch (err) {
                // already stopped / never started — nothing to clean up
            }
        }
    }

    /** Hard teardown + immediate fresh connect, for wedged connections. Debounced. */
    private forceRestart(): void {
        const now = Date.now();

        if (now - this.lastForceRestartAt < UserSignalrService.FORCE_RESTART_DEBOUNCE_MS) {
            return;
        }
        this.lastForceRestartAt = now;
        this.dropConnection();
        this.downSince = now;
        this.ensureConnected();
    }

    private scheduleRestart(): void {
        let delay = 0;

        if (this.restartTimer) {
            return;
        }
        delay = UserSignalrService.RESTART_BACKOFF_MS[
            Math.min(this.restartAttempt, UserSignalrService.RESTART_BACKOFF_MS.length - 1)
        ] + Math.floor(Math.random() * 1000);
        this.restartAttempt += 1;
        this.restartTimer = setTimeout(() => {
            this.restartTimer = null;
            this.ensureConnected();
        }, delay);
    }

    private handleConnectionUp(): void {
        this.downSince = 0;
        this.restartAttempt = 0;
        this.isUserConnected = true;
        this.signalRConnectionState.next(true);
        if (this.hasConnectedOnce) {
            // Broadcasts sent while the socket was down are lost (no replay), and pushes during the
            // Azure group-rejoin window are dropped — force a re-pull, bypassing the debounce: a
            // real reconnect ALWAYS warrants reconciling.
            this.reconcile(true);
        }
        this.hasConnectedOnce = true;
    }

    private handleConnectionDown(): void {
        if (this.downSince === 0) {
            this.downSince = Date.now();
        }
        if (document.visibilityState === 'hidden') {
            this.sawDropWhileHidden = true;
        }
        this.isUserConnected = false;
        this.signalRConnectionState.next(false);
    }

    /** Tell open surfaces to re-pull from the server because pushes may have been missed. */
    private reconcile(force: boolean): void {
        const now = Date.now();

        if (!force && now - this.lastReconcileAt < UserSignalrService.RECONCILE_DEBOUNCE_MS) {
            return;
        }
        this.lastReconcileAt = now;
        this.connectionReestablished.next();
        // Calendar surfaces (My-Day bar + workspace calendar) re-pull on this subject — mirrors the
        // reconnect nudge the old tryReconnect sent (E13 QA — AC6/AC7).
        this.calendarEventUpdates.next({ tenantId: this.appSession.tenantId });
    }

    /** Periodic health check. Runs from the watchdog worker, unthrottled even in hidden tabs. */
    private watchdogTick(): void {
        if (!this.isInitialized) {
            return;
        }
        if (!this.connection) {
            this.ensureConnected();
            return;
        }
        if (this.connection.state === HubConnectionState.Connected) {
            this.downSince = 0;
            return;
        }
        if (this.connection.state === HubConnectionState.Disconnected) {
            this.ensureConnected();
            return;
        }
        // Connecting / Reconnecting / Disconnecting for too long — a hung negotiate fetch, a wedged
        // transport, or reconnect attempts failing against a rotated token. Tear down and rebuild.
        if (this.downSince !== 0 && Date.now() - this.downSince > UserSignalrService.STUCK_LIMIT_MS) {
            this.forceRestart();
        }
    }

    private startWatchdog(): void {
        const workerSource = 'setInterval(function () { postMessage(1); }, '
            + UserSignalrService.WATCHDOG_INTERVAL_MS + ');';
        let workerBlob: Blob = null;

        // Main-thread timers in hidden tabs are throttled to ~once a minute (and paused entirely in
        // frozen tabs); dedicated-worker timers are not. Running the health check in a worker keeps
        // "always connected" true for backgrounded tabs too.
        try {
            workerBlob = new Blob([workerSource], { type: 'application/javascript' });
            this.watchdogWorkerUrl = URL.createObjectURL(workerBlob);
            this.watchdogWorker = new Worker(this.watchdogWorkerUrl);
            this.watchdogWorker.onmessage = () => this.watchdogTick();
        } catch (err) {
            // Worker unavailable (e.g. CSP) — a throttled main-thread interval still heals, just slower.
            this.watchdogFallbackTimer = setInterval(
                () => this.watchdogTick(), UserSignalrService.WATCHDOG_INTERVAL_MS);
        }
    }

    private setupWakeListeners(): void {
        this.visibilityHandler = () => this.handleVisibilityChange();
        this.wakeHandler = () => this.handleWake();
        document.addEventListener('visibilitychange', this.visibilityHandler);
        // Page Lifecycle API (Chromium): fired when a frozen tab is resumed.
        document.addEventListener('resume', this.wakeHandler);
        window.addEventListener('online', this.wakeHandler);
        window.addEventListener('focus', this.wakeHandler);
        // Back/forward-cache restore: the page returns with whatever state it was frozen with.
        window.addEventListener('pageshow', this.wakeHandler);
    }

    private handleVisibilityChange(): void {
        let hiddenForMs = 0;

        if (document.visibilityState === 'hidden') {
            this.hiddenAt = Date.now();
            return;
        }
        hiddenForMs = this.hiddenAt === 0 ? 0 : Date.now() - this.hiddenAt;
        this.hiddenAt = 0;
        this.handleWake();
        // Returning to a long-hidden tab: even if the socket LOOKS connected, throttled timers delay
        // dead-socket detection, and pushes can have been dropped silently — refresh data to be safe.
        if (this.hasConnectedOnce
            && (this.sawDropWhileHidden || hiddenForMs > UserSignalrService.STALE_AFTER_HIDDEN_MS)) {
            this.sawDropWhileHidden = false;
            this.reconcile(false);
        }
    }

    /** Common fast-path for visible/focus/online/pageshow/resume: verify the connection NOW. */
    private handleWake(): void {
        if (this.restartTimer) {
            // Don't wait out a backoff window that was scheduled while asleep/offline — retry now.
            clearTimeout(this.restartTimer);
            this.restartTimer = null;
            this.restartAttempt = 0;
        }
        if (!this.connection) {
            this.ensureConnected();
            return;
        }
        if (this.connection.state === HubConnectionState.Connected) {
            return;
        }
        if (this.connection.state === HubConnectionState.Disconnected) {
            this.restartAttempt = 0;
            this.ensureConnected();
            return;
        }
        // Mid-Connecting/Reconnecting: if it's been down a while, the next built-in retry may be a
        // stale 30s backoff away — tear down and connect fresh immediately (debounced inside).
        if (this.downSince !== 0 && Date.now() - this.downSince > UserSignalrService.WAKE_FORCE_RESTART_MS) {
            this.forceRestart();
        }
    }

    private currentAuthToken(): string {
        const utils = new UtilsService();

        return utils.getCookieValue(AppConsts.authorization.encrptedAuthTokenName) || '';
    }

    private registerUserEvents(connection: HubConnection): void {
        // NOTE: the 'notifyAutoCheckout' and 'notifyUserCheckout' handlers were removed — the "are you still
        // working?" prompt they fed was retired (product decision 2026-08-07), and both AppComponent
        // subscriptions had been empty for a long time, so nothing consumed them. The BACKEND still
        // broadcasts both: NotifyUserCheckout is POSTed by an external Azure Function
        // (SignalRAutoCheckoutEndPoint), so that endpoint must stay. Unhandled broadcasts are harmless.
        // Auto-checkout ITSELF is unaffected — MarkUserAutoCheckOutWorker still performs it server-side.
        // In/Out Status Updates
        connection.on('getUserInOut', (userDetails) => {
            if (this.appSession.tenantId === userDetails.tenantId) {
                this.userStateUpdates.next(userDetails);
            }
        });
        // Task Updates
        connection.on('getUpdatedTaskCard', (taskDetails) => {
            if (this.appSession.tenantId === taskDetails.tenantId) {
                if (taskDetails.id) {
                    this.taskUpdates.next(taskDetails);
                }
            }
        });
        // Dependency cascade — fired from TaskAppService.UpdateDates when changing a
        // blocker's dates auto-shifted one or more downstream blocked tasks. The
        // payload carries triggerTaskIds + affectedTasks so the UI can render the
        // "Shifting X also moved N downstream tasks. Undo?" toast for 10 seconds.
        connection.on('dependencyCascadeApplied', (payload) => {
            if (!payload) { return; }
            // Isolation guard: the BE broadcasts this on Clients.All, so without a filter the
            // "also moved N downstream tasks" Undo toast leaked cross-tenant to unrelated users.
            // The Undo affordance is only for the person who made the date edit, so require BOTH
            // the tenant AND the acting user to match. Read both casings (the hub protocol may
            // camelCase or PascalCase depending on JsonHubProtocol settings).
            const payloadTenantId = payload.tenantId !== undefined ? payload.tenantId : payload.TenantId;
            const payloadUserId = payload.triggerByUserId !== undefined ? payload.triggerByUserId : payload.TriggerByUserId;
            if (this.appSession.tenantId !== payloadTenantId) { return; }
            if (payloadUserId !== undefined && payloadUserId !== null && this.appSession.userId !== payloadUserId) { return; }
            this.cascadeAppliedUpdates.next(payload);
        });
        // History Updates
        connection.on('getUpdatedTaskHistory', (historyDetails) => {
            if (this.appSession.tenantId === historyDetails.tenantId) {
                if (historyDetails.id) {
                    this.historyUpdates.next(historyDetails);
                }
            }
        });
        // Newly assigned task
        connection.on('newTaskAssigned', (taskDetails) => {
            if (this.appSession.tenantId === taskDetails.tenantId && this.appSession.userId === taskDetails.AssignedToUserId) {
                if (taskDetails.id) {
                    this.newTaskAdded.next(taskDetails);
                }
            }
        });
        // Comment Updates
        connection.on('getUpdatedTaskComment', (commentDetails) => {
            if (this.appSession.tenantId === commentDetails.tenantId) {
                this.commentUpdates.next(commentDetails);
            }
        });
        // Contributor Updates — broadcast by TaskAppService for the contributor feature.
        // kind: 'taskReadyToClose' | 'taskMarkedCompleteByOther' |
        //       'stepAssignedToContributor' | 'allContributorStepsComplete' |
        //       'contributorStepForceClosed' | 'contributorAdded' | 'contributorRemoved'
        // Read tenantId in either camelCase or PascalCase — SignalR's protocol
        // may emit either depending on JsonHubProtocol settings, and we want the
        // payload to reach mine.component.ts regardless. The mine.component
        // handler also reads both cases for its own field access.
        connection.on('getContributorUpdate', (details) => {
            if (!details) {
                return;
            }
            const payloadTenantId = details.tenantId !== undefined ? details.tenantId : details.TenantId;
            if (this.appSession.tenantId === payloadTenantId) {
                this.contributorUpdates.next(details);
            }
        });
        // Event Updates
        connection.on('getUpdatedEvent', (eventDetails) => {
            if (eventDetails[0].id) {
                this.eventUpdates.next(eventDetails);
            }
        });

        // Link Token Updates
        connection.on('getTokenResponse', (tokenDetail) => {
            abp.event.trigger('app.graph.getTokenResponse',
                {
                    tokenDetail: tokenDetail.responseData
                });
        });
        // mteams presence Updates
        connection.on('updateMteamUserPresence', (statusDetails) => {
            this.mteamsStatusUpdates.next(statusDetails);
        });
        // Work Summary Required Change
        connection.on('isWorkSummaryRequiredChanged', (details) => {
            if (this.appSession.tenantId === details.tenantId) {
                this.workSummaryRequiredChanged.next(details);
            }
        });
        // Custom Project Label (Labels & Terminology): admin changed the Project label -> propagate
        // the new singular/plural to every client in this tenant in real time.
        connection.on('tenant.label.updated', (details) => {
            if (this.appSession.tenantId === details.tenantId) {
                this.labelUpdates.next(details);
            }
        });
        // Calendar & Events (E2): admin toggled the workspace master switch -> show/hide the Calendar
        // nav (+ page) for every user in this tenant in real time.
        connection.on('tenant.calendar.featureToggled', (details) => {
            if (this.appSession.tenantId === details.tenantId) {
                this.calendarFeatureUpdates.next(details);
            }
        });
        // Calendar & Events (E7): an event was created/edited/deleted in this tenant -> open calendars refresh.
        connection.on('tenant.calendar.eventChanged', (details) => {
            if (details && this.appSession.tenantId === details.tenantId) {
                this.calendarEventUpdates.next(details);
            }
        });
        // Calendar & Events (E10 / TC2.2): access or booking rights changed in this tenant -> open calendars
        // and the event form re-resolve who can be seen/booked (e.g. a newly-granted organizer appears).
        connection.on('tenant.calendar.accessChanged', (details) => {
            if (details && this.appSession.tenantId === details.tenantId) {
                this.calendarAccessUpdates.next(details);
            }
        });
        // NOTE: the 'notifyCheckinReminder' handler was removed in T4a — the check-in popup it fed is gone
        // and DayRolloverService replaces it. The BACKEND still broadcasts this until T5 removes
        // CheckinReminderWorker; an unhandled broadcast is harmless, whereas a handler with no UI is not.
        // Comment Updates
        connection.on('getUpdatedTaskTag', (taskTagDto) => {
            if (this.appSession.tenantId === taskTagDto.tenantId) {
                this.tagUpdates.next(taskTagDto);
            }
        });
        //Bot Install Updates
        connection.on('botUpdate', (details) => {
            // Per-user event (a user's own MS Teams bot subscription changed) broadcast on Clients.All.
            // Guard on BOTH tenant and user so another member of the same tenant doesn't react to it.
            if (this.appSession.tenantId === details.tenantId
                && (details.userId === undefined || details.userId === null || this.appSession.userId === details.userId)) {
                this.botUpdates.next(details);
            }
        });
        // Billing: a team member's "Ask admin to upgrade" (user-isolation group: only the owner receives it).
        connection.on('billing.upgradeRequested', (request: { requesterName: string; message: string }) => {
            this.billingUpgradeRequests.next(request);
        });
        // Import contact Updates
        connection.on('getContactImportEvent', (eventDetails) => {
            if (eventDetails && eventDetails.length > 0) {
                eventDetails.forEach(item => {
                    if (item.tenantId === this.appSession.tenantId) {
                        this.contactsImport.next(item);
                    }
                });
            }
        });

        //Watch task
        // Isolation is enforced server-side: the BE now targets the tenant-isolation group
        // (was Clients.All), so this connection only ever receives its own tenant's watch events.
        connection.on('taskWatched', (userDetails) => {
            console.log(userDetails);
            this.taskWatched.next(userDetails);
        });

        //Unwatch task
        connection.on('taskUnwatched', (userDetails) => {
            this.taskUnwatched.next(userDetails);
        });

        // Project Updates
        connection.on('getUpdatedProject', (projectDetails) => {
            if (this.appSession.tenantId === projectDetails.tenantId) {
                this.projectUpdates.next(projectDetails);
            }
        });

        connection.on('getUpdatedProjectCard', (projectDetails) => {
            if (this.appSession.tenantId === projectDetails.tenantId) {
                this.updatedProjectCard.next(projectDetails);
            }
        });

        connection.on('getPinnedProjectInfo', (projectDetails) => {
            // Pinning a project is a per-user preference broadcast on Clients.All. Guard on BOTH
            // tenant and user so other members of the tenant don't needlessly react to someone
            // else's pin/reorder.
            if (this.appSession.tenantId === projectDetails.tenantId
                && (projectDetails.userId === undefined || projectDetails.userId === null || this.appSession.userId === projectDetails.userId)) {
                this.pinnedProjectInfo.next(projectDetails);
            }
        });

        // Favorites of every type — FavoriteAppService's add / remove / reorder. Same shape of guard as
        // getPinnedProjectInfo above, for the same reason: Clients.All broadcast of a per-user preference.
        connection.on('getPinned', (favorite) => {;
            if (!favorite || this.appSession.tenantId !== favorite.tenantId) {
                return;
            }
            if (favorite.userId !== undefined && favorite.userId !== null
                && this.appSession.userId !== favorite.userId) {
                return;
            }
            this.pinnedFavoriteInfo.next(favorite);

            // Forwarded onto the legacy channel so everything already listening there — the project list
            // among them — keeps hearing about project favorites now that this event owns every add and
            // remove. Projects only: every other kind carries an entityId from a different table, and
            // passing one off as a projectId would have consumers reconciling a row that does not exist.
            if (favorite.type !== 2) { // EntityType.Project
                return;
            }

            this.pinnedProjectInfo.next({
                tenantId: favorite.tenantId,
                userId: favorite.userId,
                projectId: favorite.entityId,
                pinnedEntityId: favorite.pinnedEntityId,
                // Carried through because sortOrder means something different per action, and a consumer
                // that places the row has to know which it is holding — see the field below. Absent on
                // the legacy getPinnedProjectInfo events, which are all pin/unpin.
                action: favorite.action,
                // 'reordered': the per-project TblId = 7 position the project list sorts descending —
                // the server rewrites that order from the flat list on every drag, so this is where the
                // row belongs and it can be applied as given.
                // 'added': the flat TblId = 19 position, which is 0 for a new favorite and would sort
                // LAST under that same descending order. A consumer must place a new favorite on top by
                // its own reckoning rather than by this number.
                sortOrder: favorite.sortOrder
            });
        });

        /**
         * Cross-device task-timer sync. The backend broadcasts to the
         * `user-isolation-{userId}` SignalR group on every start/stop, so the
         * payload here is only ever for the current logged-in user — we still
         * guard on tenantId for safety against stale connections.
         */
        connection.on('getActiveTimerUpdate', (payload) => {
            if (!payload) {
                return;
            }
            if (payload.tenantId !== undefined && payload.tenantId !== null
                && this.appSession.tenantId !== payload.tenantId) {
                return;
            }
            this.timerUpdates.next(payload);
        });

        // A client changed in this workspace. The server sends it to the tenant's group after every client save
        // (ClientChangePublisher) and from the Outlook ingest. The tab that made the save is named in originTabId and
        // has already patched its own rows, so its echo stops here, before any consumer sees it
        // (CLIENTS_CROSS_USER_UPDATES_TEST_CASES.md). The tenant check stays as a second guard.
        connection.on('getClientUpdate', (payload: ClientUpdateSignal) => {
            switch (routeClientUpdate(payload, this.appSession.tenantId, isFromThisTab)) {
                case 'leadSummary':
                    this.leadSummaryUpdates.next(payload);
                    return;
                case 'leadStages':
                    this.leadStageUpdates.next(payload);
                    return;
                case 'lostReasons':
                    this.lostReasonUpdates.next(payload);
                    return;
                case 'clients':
                    this.clientUpdates.next(payload);
                    return;
                default:
                    return;
            }
        });

        // Conversation counts may have changed (Global Conversations S2 §4.6). The server sends it only to the
        // user-isolation groups of the write's audience, with ids only. acceptConversationSignal drops a missing
        // payload, another workspace and this tab's own echo: originTabId names a tab only when that tab's answer
        // already carried the counts (SetReadState, AutoReadAmbient, Undo), so any other write from this tab still
        // arrives and its counts reconcile (SD2-10). Not wrapped in NgZone.run: the consumers re-enter the zone.
        connection.on(CONVERSATION_COUNTS_CHANGED_EVENT, (payload: ConversationCountsChangedSignal) => {
            if (!acceptConversationSignal(payload, this.appSession.tenantId, isFromThisTab)) {
                return;
            }
            this.conversationCountsChanged.next(payload);
        });

        // A discovery job of this workspace changed its status or stage (Adaptive Onboarding contract §7; the onboarding
        // scan and the Organization Intelligence page). Content-free (ids, status, stage) and sent to the workspace's
        // tenant group; acceptDiscoveryJobSignal is the client-side tenant guard (another workspace, no real job or an
        // unknown entry point is dropped). DiscoveryJobStatusService re-reads DiscoveryJobs/GetCurrent and re-enters the
        // zone itself, so this stays outside it.
        connection.on(DISCOVERY_JOB_CHANGED_EVENT, (payload: DiscoveryJobChangedSignal) => {
            if (!acceptDiscoveryJobSignal(payload, this.appSession.tenantId)) {
                return;
            }
            this._discoveryJobStatus.acceptSignal(payload);
        });
    }
}
